import { useEffect, useState } from 'react'
import { useStores } from '../../../stores/store'
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { FaEdit } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { parseLicenseJson } from '../../common/functions';
import Loader from '../../common/loader';
import { config } from '../../../config';
import Popup from 'reactjs-popup';

function AdminSchoolDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const referralId = searchParams.get('referral_id');

  const { auth } = useStores();
  const [isDataError, setIsDataError] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [school, setSchool] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [editingDevice, setEditingDevice] = useState<any>(null);
  const [editDeviceName, setEditDeviceName] = useState("");
  const [deletingDevice, setDeletingDevice] = useState<any>(null);

  const refreshData = () => {
    setIsDataLoading(true);
    auth.getSchool(id).then((res) => {
      setSchool(res.data);
      setIsDataLoading(false);
    }).catch((error) => {
      setIsDataLoading(false);
      setIsDataError(true);
    });
  };

  useEffect(() => {
    refreshData();
  }, []);

  const handleAssignToDevice = async () => {
    try {
      await auth.associateActivationCode(id, referralId, selectedDeviceId);
      setSelectedDeviceId(null);
      navigate('/admin/codes/');
    } catch (error) {
      console.error('Error assigning license:', error);
      alert(t('licenseAssignError'));
      setSelectedDeviceId(null);
    }
  };

  const handleResetLicense = async (licenseId, licenseCode) => {
    if (!window.confirm(t('confirmResetActivation'))) {
      return;
    }

    try {
      await auth.api.post(`${config.getEndpointUri()}/registry/activation-codes/${licenseId}/reset/`);
      alert(t('licenseResetSuccess'));
      refreshData();
    } catch (error) {
      console.error('Error resetting license:', error);
      alert(t('licenseResetError'));
    }
  };

  const handleDeleteDevice = async () => {
    if (!deletingDevice) return;
    try {
      await auth.api.post(`${config.getEndpointUri()}/registry/devices/${deletingDevice.id}/delete_device/`);
      setDeletingDevice(null);
      alert(t('deviceDeleteSuccess'));
      refreshData();
    } catch (error) {
      console.error('Error deleting device:', error);
      alert(t('deviceDeleteError'));
      setDeletingDevice(null);
    }
  };

  const handleEditDeviceName = async (e) => {
    e.preventDefault();
    if (!editingDevice) return;

    try {
      await auth.api.patch(`${config.getEndpointUri()}/registry/devices/${editingDevice.id}/`, {
        device_custom_name: editDeviceName
      });
      setEditingDevice(null);
      setEditDeviceName("");
      refreshData();
    } catch (error) {
      console.error('Error editing device name:', error);
      alert(t('errorEditingDevice'));
    }
  };

  if (isDataLoading)
    return <div className='grid h-screen place-items-center'><Loader /></div>

  if (isDataError)
    return <div className='grid h-screen place-items-center'>{t('cannotLoadPage')}</div>

  return (
		<div className="w-full flex flex-col p-6 items-start justify-start gap-4">
        <Popup
          open={editingDevice != null}
          position="right center"
          closeOnDocumentClick
          onClose={() => setEditingDevice(null)}
        >
          <form onSubmit={handleEditDeviceName} className="flex flex-col items-center gap-6 p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-xl font-bold">{t('editDeviceName')}</h2>
            {editingDevice && (
              <div className="w-full text-center">
                <p><strong>{t('deviceIdentifier')}:</strong> {editingDevice.device_identifier}</p>
              </div>
            )}
            <div className="w-full">
              <label className="block text-sm font-medium mb-2">{t('deviceName')}</label>
              <input
                value={editDeviceName}
                onChange={(e) => setEditDeviceName(e.target.value)}
                className="shadow appearance-none border rounded w-full p-2 focus:outline-none focus:shadow-outline"
              />
            </div>
            <div className="w-full flex flex-row justify-center gap-6">
              <button className="bg-primary text-white font-bold py-2 px-4 rounded-full" type="submit">{t('save')}</button>
              <button
                onClick={(e) => { e.preventDefault(); setEditingDevice(null) }}
                className="border-2 border-primary text-primary font-bold py-2 px-4 rounded-full"
              >
                {t('cancel')}
              </button>
            </div>
          </form>
        </Popup>

        <Popup open={selectedDeviceId != null}
          position="right center"
          closeOnDocumentClick
          onClose={() => setSelectedDeviceId(null)}
        >
          <div className="flex flex-col items-center gap-6 p-6">
            <span>{t('associateLicence')}</span>
            <div className="flex flex-row items-center gap-6">
              <button onClick={handleAssignToDevice} className="bg-primary text-white font-bold py-2 px-4 rounded-full">{t('yes')}</button>
              <button onClick={() => setSelectedDeviceId(null)} className="border-2 border-primary text-primary font-bold py-2 px-4 rounded-full">{t('no')}</button>
            </div>
          </div>
        </Popup>

        <Popup
          open={deletingDevice != null}
          position="right center"
          closeOnDocumentClick
          onClose={() => setDeletingDevice(null)}
        >
          <div className="flex flex-col items-center gap-6 p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-xl font-bold">{t('deleteDevice')}</h2>
            {deletingDevice && (
              <div className="w-full text-center">
                <p><strong>{deletingDevice.device_custom_name || t('noName')}</strong></p>
                <p className="text-sm text-gray-500">{deletingDevice.device_identifier}</p>
              </div>
            )}
            <p className="text-center">{t('confirmDeleteDevice')}</p>
            <div className="flex flex-row items-center gap-6">
              <button onClick={handleDeleteDevice} className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-full">{t('yes')}</button>
              <button onClick={() => setDeletingDevice(null)} className="border-2 border-primary text-primary font-bold py-2 px-4 rounded-full">{t('no')}</button>
            </div>
          </div>
        </Popup>

        <Link className="bg-primary text-white rounded-3xl p-4" to={referralId ? `/admin/schools/?referral_id=${referralId}` : `/admin/schools/`}>
          {t('back')}
        </Link>

        <div className="w-full flex flex-row gap-4">
          <div className='flex flex-col flex-1'>
              <span className='text-2xl'>{t('accountDetails')}</span>
              <div className="flex flex-col border-2 border-primary rounded-3xl p-4">
                  <span className='font-bold'>{t('name')}: { school?.name }</span>
                  <span>{t('email')}: { school?.email }</span>
                  <span>{t('address')}: { school?.address }</span>
                  <span>{t('city')}: { school?.city }</span>
                  <span>{t('province')}: { school?.province }</span>
                  <span>{t('region')}: { school?.regione }</span>
                  <span>{t('code')}: { school?.code }</span>
                  <span>{t('user')}:&nbsp;
                    {
                      school?.users?.filter(user => user.type == 3).map((user) =>
                        <span>{user.pretty_name} - {user.email}</span>
                      )
                    }
                  </span>
              </div>
          </div>
          <div className='flex flex-col flex-1'>
              <span className='text-2xl'>{t('users')}</span>
              <div className="flex flex-col border-2 border-primary rounded-3xl p-4">
              {school?.users?.filter(user => user.type == 2).map((user) =>
                  <div>{ user.pretty_name } - {user.email} </div>
                )}
              </div>
          </div>
        </div>

        {school?.devices && school.devices.length > 0 ? (
          <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {school.devices.map((device) =>
              <div key={device.id} className='flex flex-col'>
                <span className='text-2xl flex items-center gap-2'>
                  {device.device_custom_name || t('noName')}
                  <button
                    onClick={() => { setEditingDevice(device); setEditDeviceName(device.device_custom_name || ""); }}
                    className="text-gray-500 hover:text-gray-700"
                    title={t('editDeviceName')}
                  >
                    <FaEdit size={16} />
                  </button>
                </span>
                <div className="flex flex-col border-2 border-primary rounded-3xl p-4 gap-2">
                  <div><strong>{t('deviceIdentifier')}:</strong> {device.device_identifier}</div>

                  {referralId && (
                    <button
                      onClick={() => setSelectedDeviceId(device.id)}
                      className="bg-primary text-white font-bold py-2 px-4 rounded-full mt-2"
                    >
                      {t('assign')}
                    </button>
                  )}

                  {device.activation_codes && device.activation_codes.length > 0 && (
                    <div className="mt-2">
                      <div className="font-bold mb-2">{t('licenses')}:</div>
                      <div className="flex flex-col gap-3">
                        {device.activation_codes.map((lic) => (
                          <div key={lic.id} className="border-l-2 border-gray-300 pl-4 flex flex-col gap-2">
                            <div><strong>{t('code')}:</strong> {lic.code}</div>
                            <div><strong>{t('type')}:</strong> {lic.type === 1 ? 'Pinguin' : lic.type === 2 ? 'Miri' : lic.type === 3 ? 'Miri Pro' : t('unknown')}</div>
                            {lic.license_data && (
                              <div className="text-gray-600 text-sm">{parseLicenseJson(lic.license_data)}</div>
                            )}
                            <button
                              onClick={() => handleResetLicense(lic.id, lic.code)}
                              className="border-2 border-red-500 hover:border-red-700 text-red-500 hover:text-red-700 font-bold py-1 px-3 rounded text-sm mt-2 transition-colors"
                            >
                              {t('reset')}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => setDeletingDevice(device)}
                    className="border-2 border-black hover:border-red-700 text-black hover:text-red-700 font-bold py-1 px-3 rounded text-sm mt-4 transition-colors"
                  >
                    {t('deleteDevice')}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className='flex flex-col'>
            <span className='text-2xl'>{t('devices')}</span>
            <div className="flex flex-col border-2 border-primary rounded-3xl p-4">
              <div>{t('noDevices')}</div>
            </div>
          </div>
        )}
		</div>
  )
}

export default AdminSchoolDetailPage;
